import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogContent,
  Button,
  Typography,
  Box,
  CircularProgress,
  IconButton,
  Fade,
  Paper,
  alpha
} from '@mui/material';
import {
  Close,
  CheckCircle,
  Cancel,
  TrendingUp,
  Assessment,
  Speed,
  Group
} from '@mui/icons-material';
import { UserPermission, PermissionKey } from '../types/admin';
import { useUserPermissions } from '../hooks/useUserPermissions';

interface Props {
  open: boolean;
  onClose: () => void;
  user: {
    id: number;
    username: string;
  };
  onSuccess?: () => void;
}

const DARK_COLORS = {
  SURFACE: "#121212",
  CARD: "#1a1a1a",
  ACCENT: "#2d2d2d",
  TEXT_PRIMARY: "#ffffff",
  TEXT_SECONDARY: "#b0b0b0",
  BORDER: "#333333",
  SUCCESS: "#00e676",
  ERROR: "#ff1744",
  HOVER: "#404040",
};

const PERMISSION_CONFIG: Record<string, {
  icon: React.ComponentType<any>;
  label: string;
  description: string;
  color: string;
}> ={
 economics: {
icon: TrendingUp,
label: 'Economia',
 description: 'Accesso a dati e report economici',
color: '#00e676'
 },
 consuntivi: {
icon: Assessment,
label: 'Consuntivi',
description: 'Visualizzazione e gestione dei report consuntivi',
color: '#00b0ff' },
 efficienza: {
icon: Speed,
 label: 'Efficienza',
 description: 'Accesso alle metriche di efficienza',
 color: '#ffea00'
},
 risorse: {
icon: Group,
label: 'Risorse',
description: 'Gestione delle risorse e del personale',
 color: '#ff6e40'
 }
};

export const UserPermissionsDialog: React.FC<Props> = ({
  open,
  onClose,
  user,
  onSuccess
}) => {
  const { loading, error, fetchPermissions, updatePermissions } = useUserPermissions();
  const [permissions, setPermissions] = useState<Record<string, boolean>>({});
  const [saving, setSaving] = useState(false);
  const [hasChanges, setHasChanges] = useState(false);

  useEffect(() => {
    if (open && user.id) {
      const loadPermissions = async () => {
        const result = await fetchPermissions(user.id);
        if (result) {
          const permissionData: Record<string, boolean> = {};
          Object.keys(PERMISSION_CONFIG).forEach(key => {
            permissionData[key] = (result as any)[key] || false;
          });
          setPermissions(permissionData);
          setHasChanges(false);
        }
      };
      loadPermissions();
    }
  }, [open, user.id, fetchPermissions]);

  const handleTogglePermission = (permissionKey: string) => {
    setPermissions(prev => ({
      ...prev,
      [permissionKey]: !prev[permissionKey]
    }));
    setHasChanges(true);
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const result = await updatePermissions(user.id, permissions as any);
      if (result) {
        onSuccess?.();
        onClose();
      }
    } catch (err) {
      // Error updating permissions
    } finally {
      setSaving(false);
    }
  };

  const handleClose = () => {
    if (!saving) {
      onClose();
    }
  };

  if (!open) return null;

  return (
    <Dialog 
      open={open} 
      onClose={handleClose}
      maxWidth="md" 
      fullWidth
      PaperProps={{
        sx: {
          bgcolor: DARK_COLORS.SURFACE,
          borderRadius: 3,
          boxShadow: '0 24px 48px rgba(0,0,0,0.6)',
          backgroundImage: 'linear-gradient(to bottom, rgba(255,255,255,0.02), transparent)',
        }
      }}
    >
      {/* Header */}
      <Box sx={{ 
        p: 3, 
        pb: 2,
        borderBottom: `1px solid ${DARK_COLORS.BORDER}`,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between'
      }}>
        <Box>
          <Typography variant="h5" sx={{ 
            color: DARK_COLORS.TEXT_PRIMARY, 
            fontWeight: 700,
            mb: 0.5,
            letterSpacing: '-0.5px'
          }}>
            Gestione Permessi
          </Typography>
          <Typography variant="body2" sx={{ 
            color: DARK_COLORS.TEXT_SECONDARY,
            display: 'flex',
            alignItems: 'center',
            gap: 1
          }}>
            Utente: <Box component="span" sx={{ 
              color: DARK_COLORS.TEXT_PRIMARY,
              fontWeight: 600,
              bgcolor: alpha(DARK_COLORS.SUCCESS, 0.1),
              px: 1.5,
              py: 0.5,
              borderRadius: 2
            }}>{user.username}</Box>
          </Typography>
        </Box>
        <IconButton 
          onClick={handleClose}
          disabled={saving}
          sx={{ 
            color: DARK_COLORS.TEXT_SECONDARY,
            '&:hover': { 
              bgcolor: alpha(DARK_COLORS.ERROR, 0.1),
              color: DARK_COLORS.ERROR
            }
          }}
        >
          <Close />
        </IconButton>
      </Box>

      <DialogContent sx={{ p: 3 }}>
        {loading ? (
          <Box display="flex" justifyContent="center" alignItems="center" py={8}>
            <CircularProgress sx={{ color: DARK_COLORS.SUCCESS }} />
          </Box>
        ) : error ? (
          <Box display="flex" flexDirection="column" alignItems="center" py={8}>
            <Cancel sx={{ fontSize: 64, color: DARK_COLORS.ERROR, mb: 2 }} />
            <Typography color={DARK_COLORS.ERROR} variant="body1">
              {error}
            </Typography>
          </Box>
        ) : (
          <Box sx={{ display: 'grid', gap: 2 }}>
            {Object.entries(PERMISSION_CONFIG).map(([key, config]) => {
              const IconComponent = config.icon;
              const isEnabled = permissions[key] || false;
              
              return (
                <Fade in key={key} timeout={300}>
                  <Paper
                    elevation={0}
                    sx={{
                      p: 2.5,
                      bgcolor: DARK_COLORS.CARD,
                      border: `2px solid ${isEnabled ? config.color : DARK_COLORS.BORDER}`,
                      borderRadius: 2,
                      cursor: 'pointer',
                      transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                      position: 'relative',
                      overflow: 'hidden',
                      '&:hover': {
                        bgcolor: DARK_COLORS.ACCENT,
                        transform: 'translateY(-2px)',
                        boxShadow: `0 8px 24px ${alpha(config.color, 0.2)}`
                      },
                      '&::before': {
                        content: '""',
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        right: 0,
                        height: 4,
                        bgcolor: config.color,
                        opacity: isEnabled ? 1 : 0,
                        transition: 'opacity 0.3s'
                      }
                    }}
                    onClick={() => handleTogglePermission(key)}
                  >
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                      {/* Icon */}
                      <Box sx={{
                        width: 56,
                        height: 56,
                        borderRadius: 2,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        bgcolor: alpha(config.color, isEnabled ? 0.15 : 0.05),
                        transition: 'all 0.3s',
                        flexShrink: 0
                      }}>
                        <IconComponent sx={{ 
                          fontSize: 32, 
                          color: isEnabled ? config.color : DARK_COLORS.TEXT_SECONDARY,
                          transition: 'all 0.3s'
                        }} />
                      </Box>

                      {/* Content */}
                      <Box sx={{ flex: 1, minWidth: 0 }}>
                        <Typography sx={{ 
                          color: DARK_COLORS.TEXT_PRIMARY,
                          fontWeight: 600,
                          fontSize: '1.1rem',
                          mb: 0.5,
                          display: 'flex',
                          alignItems: 'center',
                          gap: 1
                        }}>
                          {config.label}
                        </Typography>
                        <Typography sx={{ 
                          color: DARK_COLORS.TEXT_SECONDARY,
                          fontSize: '0.9rem',
                          lineHeight: 1.5
                        }}>
                          {config.description}
                        </Typography>
                      </Box>

                      {/* Toggle Indicator */}
                      <Box sx={{
                        width: 48,
                        height: 48,
                        borderRadius: '50%',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        bgcolor: isEnabled 
                          ? alpha(DARK_COLORS.SUCCESS, 0.15) 
                          : alpha(DARK_COLORS.ERROR, 0.1),
                        transition: 'all 0.3s',
                        flexShrink: 0
                      }}>
                        {isEnabled ? (
                          <CheckCircle sx={{ 
                            color: DARK_COLORS.SUCCESS, 
                            fontSize: 28 
                          }} />
                        ) : (
                          <Cancel sx={{ 
                            color: DARK_COLORS.TEXT_SECONDARY, 
                            fontSize: 28 
                          }} />
                        )}
                      </Box>
                    </Box>
                  </Paper>
                </Fade>
              );
            })}
          </Box>
        )}
      </DialogContent>

      {/* Footer Actions */}
      <Box sx={{ 
        p: 3, 
        pt: 2,
        borderTop: `1px solid ${DARK_COLORS.BORDER}`,
        display: 'flex',
        gap: 2,
        justifyContent: 'flex-end'
      }}>
        <Button
          onClick={handleClose}
          disabled={saving}
          sx={{
            color: DARK_COLORS.TEXT_SECONDARY,
            textTransform: 'none',
            fontWeight: 600,
            px: 3,
            '&:hover': {
              bgcolor: alpha(DARK_COLORS.HOVER, 0.5)
            }
          }}
        >
          Annulla
        </Button>
        <Button
          onClick={handleSave}
          disabled={saving || loading || !hasChanges || !!error}
          variant="contained"
          sx={{
            bgcolor: DARK_COLORS.SUCCESS,
            color: '#000',
            textTransform: 'none',
            fontWeight: 700,
            px: 4,
            boxShadow: hasChanges ? `0 4px 16px ${alpha(DARK_COLORS.SUCCESS, 0.4)}` : 'none',
            '&:hover': {
              bgcolor: DARK_COLORS.SUCCESS,
              boxShadow: `0 6px 20px ${alpha(DARK_COLORS.SUCCESS, 0.5)}`,
              transform: 'translateY(-1px)'
            },
            '&:disabled': {
              bgcolor: DARK_COLORS.ACCENT,
              color: DARK_COLORS.TEXT_SECONDARY
            },
            transition: 'all 0.3s'
          }}
        >
          {saving ? (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <CircularProgress size={20} sx={{ color: '#000' }} />
              Salvataggio...
            </Box>
          ) : (
            'Salva Modifiche'
          )}
        </Button>
      </Box>
    </Dialog>
  );
};